import * as React from "react";
import { throttle } from "throttle-debounce";
import { CancellablePromise } from "../../Generic/Promises";
import LargeListViewer from "./LargeListViewer";

export type LargeListHiddenElement = React.ReactElement<{ style: React.CSSProperties; ref: React.Ref<HTMLElement>; }>;

type Props = typeof LargeList.defaultProps & {
    elementHeight: number;
    hiddenElement?: LargeListHiddenElement;
    relative?: boolean;
    position?: number;
    height?: number;
    renderIndex: (index: number) => React.ReactNode;
    indexCount: number;
    element: HTMLElement | null;
    onVisibleCountChange?: (visible: number) => void;
};

interface State {
    visible: {
        start: number;
        end: number;
    };
    hidden: {
        before: number;
        after: number;
    };
    offsetTop: number;
    loading: boolean;
}

interface VisibilityResults {
    start: number;
    end: number;
    after: number;
    before: number;
}

export default class LargeList extends React.Component<Props, State> {
    public static defaultProps = {
        topPadding      : 0,
        visibleThreshold: 10,
    };

    protected top: React.RefObject<HTMLElement | null>;
    protected cancellablePromise: null | CancellablePromise<VisibilityResults>;

    constructor(props: Props) {
        super(props);

        this.state = {
            visible: {
                start: 0,
                end  : 0,
            },
            hidden: {
                before: 0,
                after : 0,
            },
            offsetTop: 0,
            loading  : false,
        };

        this.top = React.createRef();

        this.cancellablePromise = null;

        this.handleScroll = this.handleScroll.bind(this);
        this.updateVisible = this.updateVisible.bind(this);
        this.calculateVisible = throttle(250, this.calculateVisible.bind(this));
    }

    public componentDidMount(): void {
        // Add the scroll event to the Body
        // this.offsetTop = this.top.getBoundingClientRect().top - 50;
        if(this.top.current) {
            this.setState({
                offsetTop: this.top.current.getBoundingClientRect().top - this.props.topPadding,
            });
        }

        if(!this.props.relative) {
            if(this.props.element !== null) {
                this.props.element.addEventListener("scroll", this.handleScroll);
            }
            // this.context.body.contentRef.addEventListener("scroll", this.handleScroll);
        }

        this.updateVisible();
    }

    public componentWillUnmount(): void {
        // Remove the scroll event from the Window
        if(!this.props.relative) {
            // this.context.body.contentRef.removeEventListener("scroll", this.handleScroll);
            if(this.props.element !== null) {
                this.props.element.removeEventListener("scroll", this.handleScroll);
            }
        }

        if(this.cancellablePromise) {
            this.cancellablePromise.cancel();
        }
    }

    protected replaceListener(prevElement: HTMLElement | null, newElement: HTMLElement | null): void {
        if(prevElement !== null) {
            prevElement.removeEventListener("scroll", this.handleScroll);
        }

        if(!this.props.relative && newElement !== null) {
            newElement.addEventListener("scroll", this.handleScroll);
        }
    }

    protected handleScroll(): void {
        if(this.state.loading) return;

        this.updateVisible();
    }

    protected calculateVisible(
        callback: (results: VisibilityResults) => void
    ): void {
        if(this.props.element === null) {
            callback({
                start : 0,
                end   : 0,
                after : 0,
                before: 0,
            });

            return;
        } else if(process.env.NODE_ENV === "test") {
            callback({
                start : 0,
                end   : this.props.indexCount,
                after : 0,
                before: 0,
            });
        }

        const threshold = this.props.visibleThreshold;

        let position;
        let windowHeight;
        let offset = 0;

        if(this.props.position) {
            position = this.props.position - this.state.offsetTop;
        } else {
            // position = this.context.body.contentRef.scrollTop - this.state.offsetTop;
            position = this.props.element.scrollTop - this.state.offsetTop;
        }

        if(this.props.height) {
            windowHeight = this.props.height;
        } else {
            windowHeight = document.body.scrollHeight - this.props.topPadding;
        }

        if(this.top.current) {
            offset = this.top.current.getBoundingClientRect().top;
        }

        // Determine the start and end positions
        let start = Math.floor(position / this.props.elementHeight);
        let end = Math.floor(windowHeight / this.props.elementHeight + start);

        if(start - threshold >= 0 && offset <= 0) {
            start -= threshold;
        } else {
            start = 0;
        }

        if(end + threshold <= this.props.indexCount) {
            end += threshold;
        } else {
            end = this.props.indexCount;
        }

        if(this.props.indexCount <= threshold) {
            start = 0;
            end = this.props.indexCount;
        }

        // Determine the before and after heights
        const before = Math.floor(start * this.props.elementHeight);
        const after = Math.floor((this.props.indexCount - end) * this.props.elementHeight);

        callback({
            start : start,
            end   : end,
            after : after,
            before: before,
        });
    }

    protected updateVisible(): void {
        if(this.cancellablePromise) {
            this.cancellablePromise.cancel();
        }

        // NOTE: It is done this way to stop issues with the debounced function being called after unmounting
        this.cancellablePromise = new CancellablePromise(
            new Promise(resolve => {
                this.calculateVisible(resolve);
            })
        );

        this.cancellablePromise
            .promise
            .then(results => {
                const { start, end, before, after } = results;

                let visibleCallback = () => { /* no-op */ };

                if(typeof this.props.onVisibleCountChange === "function") {
                    const visible = end - start;
                    const oldVisible = this.state.visible.end - this.state.visible.start;

                    if(visible !== oldVisible) {
                        visibleCallback = () => {
                            if(typeof this.props.onVisibleCountChange === "function") {
                                this.props.onVisibleCountChange(visible);
                            }
                        };
                    }
                }

                this.setState({
                    visible: {
                        start: start,
                        end  : end,
                    },
                    hidden: {
                        before: before,
                        after : after,
                    },
                }, visibleCallback);
            })
            .catch(err => {
                if(!err || (err && !err.cancelled)) {
                    console.error("Error calculating visible", err);
                }
            });
    }

    public componentDidUpdate(prevProps: Readonly<Props>): void {
        if(prevProps.element !== this.props.element) {
            this.replaceListener(prevProps.element, this.props.element);
        }

        if(prevProps.indexCount !== this.props.indexCount) {
            this.updateVisible();
        } else if(
            prevProps.height !== this.props.height ||
            prevProps.position !== this.props.position
        ) {
            this.updateVisible();
        }
    }

    public render(): React.ReactNode {
        let hiddenEl = this.props.hiddenElement;

        if(typeof hiddenEl === "undefined") {
            hiddenEl = <div />;
        }

        // Ensure that the child always has the same parity (even, odd)
        let extra = null;

        if(this.state.visible.start % 2 !== 0) {
            extra = React.cloneElement(hiddenEl, {
                style: {
                    display: "none",
                },
            });
        }

        // Render the children
        return (
            <React.Fragment>
                {extra}
                {React.cloneElement(hiddenEl, {
                    style: {
                        height: this.state.hidden.before,
                    },
                    ref: this.top,
                })}
                <LargeListViewer
                    start={this.state.visible.start}
                    end={this.state.visible.end}
                    renderIndex={this.props.renderIndex}
                    indexCount={this.props.indexCount}
                />
                {React.cloneElement(hiddenEl, {
                    style: {
                        height: this.state.hidden.after,
                    },
                })}
            </React.Fragment>
        );
    }
}
